import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    useTheme,
    Stack,
    Button,
    CircularProgress,
    Tooltip,
    Snackbar,
    Radio,
    Divider,
    Card,
    CardContent,
    FormControl,
    FormControlLabel,
    Switch,
    TextField,
    Grid,
    Chip,
    IconButton,
    Collapse,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import { collectionPointsService, CollectionPoint, CollectionPointPurposeMap, CollectionPointPurposeConstant } from '../../services/collectionpoints-service';
import { purposeService, Purpose } from '../../services/purpose-service';
import { config } from '../../config/config';

/**
 * PurposesTab Component
 * 
 * API Call Optimization:
 * - /collectionpoints/:id/purposes is called only once when the tab is first accessed
 * - After adding/removing purpose mappings, local state is updated optimistically
 * - No redundant API calls are made for refreshing mapped purposes
 * - Optimistic updates are reverted on API errors
 */
// Props interface for the PurposesTab component
interface PurposesTabProps {
    collectionPoint: CollectionPoint | null;
    isPublished: boolean;
    onPurposesChanged?: () => void; // Callback when purposes are successfully changed
    onHasChanges?: (hasChanges: boolean) => void; // Callback to notify parent of unsaved changes
    onUserChanges?: (hasUserChanges: boolean) => void; // Callback to notify parent of user changes for tab indicator
    persistedFormState?: any; // Form state persisted from parent
    onFormStateChange?: (formState: any) => void; // Callback to persist form state to parent
    persistedData?: {
        purposes: any[];
        purposeConstants: any[];
        mappedPurposes: any[];
        hasLoadedInitialData: boolean;
        hasAttemptedLoadMappedPurposes: boolean;
    }; // Data persisted from parent
    onDataChange?: (data: any) => void; // Callback to persist data to parent
}

const PurposesTab: React.FC<PurposesTabProps> = ({ 
    collectionPoint, 
    isPublished, 
    onPurposesChanged, 
    onHasChanges,
    onUserChanges,
    persistedFormState,
    onFormStateChange,
    persistedData,
    onDataChange
}) => {
    const theme = useTheme();

    // Data loading states - initialize with persisted data if available
    const [purposes, setPurposes] = useState<Purpose[]>(persistedData?.purposes || []);
    const [loadingPurposes, setLoadingPurposes] = useState(false);
    const [hasAttemptedLoadPurposes, setHasAttemptedLoadPurposes] = useState(false);
    const [purposeConstants, setPurposeConstants] = useState<CollectionPointPurposeConstant[]>(persistedData?.purposeConstants || []);
    const [loadingConstants, setLoadingConstants] = useState(false);
    const [hasAttemptedLoadConstants, setHasAttemptedLoadConstants] = useState(false);
    const [mappedPurposes, setMappedPurposes] = useState<CollectionPointPurposeMap[]>(persistedData?.mappedPurposes || []);
    const [loadingMappedPurposes, setLoadingMappedPurposes] = useState(false);
    const [hasAttemptedLoadMappedPurposes, setHasAttemptedLoadMappedPurposes] = useState(persistedData?.hasAttemptedLoadMappedPurposes || false);
    
    // Track if initial data has been loaded to prevent redundant calls
    const [hasLoadedInitialData, setHasLoadedInitialData] = useState(persistedData?.hasLoadedInitialData || false);
    
    // Track if user has made changes to the form
    const [hasUserChanges, setHasUserChanges] = useState(false);
    
    // Track the last collection point ID to detect actual changes
    const [lastCollectionPointId, setLastCollectionPointId] = useState<number | null>(
        persistedData?.hasLoadedInitialData ? collectionPoint?.CollectionPointId || null : null
    );

    // UI state
    const [selectedPurpose, setSelectedPurpose] = useState<Purpose | null>(null);
    const [availablePurposes, setAvailablePurposes] = useState<Purpose[]>([]);

    // Form state
    const [lawfulGroundId, setLawfulGroundId] = useState<number | ''>('');
    const [purposeCategoryId, setPurposeCategoryId] = useState<number | ''>('');
    const [isRequired, setIsRequired] = useState(false);
    const [retentionPeriodTypeId, setRetentionPeriodTypeId] = useState<number | ''>('');
    const [timeBasedPeriod, setTimeBasedPeriod] = useState<number | ''>('');
    const [timeBasedUnitId, setTimeBasedUnitId] = useState<number | ''>('');
    const [startDateTypeId, setStartDateTypeId] = useState<number | ''>('');
    const [customStartDate, setCustomStartDate] = useState<string>('');
    const [extensionAllowed, setExtensionAllowed] = useState(false);
    const [extensionReason, setExtensionReason] = useState('');
    const [endOnPurposeFulfilled, setEndOnPurposeFulfilled] = useState(false);
    const [endOnConsentWithdrawn, setEndOnConsentWithdrawn] = useState(false);
    const [endOnRetentionExpired, setEndOnRetentionExpired] = useState(false);
    const [endOnErasureRequested, setEndOnErasureRequested] = useState(false);
    const [legalRetentionRequired, setLegalRetentionRequired] = useState(false);
    const [legalRetentionPeriod, setLegalRetentionPeriod] = useState<number | ''>('');
    const [endOnAccountClosure, setEndOnAccountClosure] = useState(false);

    // Validation state
    const [lawfulGroundError, setLawfulGroundError] = useState<string>('');
    const [timeBasedPeriodError, setTimeBasedPeriodError] = useState<string>('');
    const [timeBasedUnitError, setTimeBasedUnitError] = useState<string>('');
    const [customStartDateError, setCustomStartDateError] = useState<string>('');
    const [legalRetentionPeriodError, setLegalRetentionPeriodError] = useState<string>('');
    const [endConditionsError, setEndConditionsError] = useState<string>('');
    const [hasAttemptedPurposeValidation, setHasAttemptedPurposeValidation] = useState(false);

    // Success/error messages
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Loading state for save operation
    const [isSaving, setIsSaving] = useState(false);

    // Loading state for delete operations
    const [deletingMappingIds, setDeletingMappingIds] = useState<Set<number>>(new Set());

    // UI state for expandable cards
    const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

    // Computed values
    const isCustomDateSelected = !!purposeConstants.find(
        c => c.Category === 'START_DATE_TYPE' && c.ConstantId === startDateTypeId && c.Code === 'CUSTOM'
    );

    // Form utility functions - moved from parent component
    const resetPurposeMappingForm = () => {
        setLawfulGroundId('');
        setPurposeCategoryId('');
        setIsRequired(false);
        setRetentionPeriodTypeId('');
        setTimeBasedPeriod('');
        setTimeBasedUnitId('');
        setStartDateTypeId('');
        setCustomStartDate('');
        setExtensionAllowed(false);
        setExtensionReason('');
        setEndOnPurposeFulfilled(false);
        setEndOnConsentWithdrawn(false);
        setEndOnRetentionExpired(false);
        setEndOnErasureRequested(false);
        setLegalRetentionRequired(false);
        setLegalRetentionPeriod('');
        setEndOnAccountClosure(false);
        
        // Clear validation errors
        setLawfulGroundError('');
        setTimeBasedPeriodError('');
        setTimeBasedUnitError('');
        setCustomStartDateError('');
        setLegalRetentionPeriodError('');
        setEndConditionsError('');
        setHasAttemptedPurposeValidation(false);
        
        // Reset user changes flag
        setHasUserChanges(false);
    };

    // Data loading functions - moved from parent component
    const loadPurposes = async () => {
        try {
            setLoadingPurposes(true);
            setHasAttemptedLoadPurposes(true);
            const response = await purposeService.getPurposes({
                filters: {
                    isPublished: true  // Only get published purposes
                }
            });
            setPurposes(response.data);
            
            // Persist data to parent
            if (onDataChange) {
                onDataChange((prev: any) => ({
                    ...prev,
                    purposes: response.data
                }));
            }
        } catch (error) {
            console.error('Error loading purposes:', error);
        } finally {
            setLoadingPurposes(false);
        }
    };

    const loadPurposeConstants = async () => {
        try {
            setLoadingConstants(true);
            setHasAttemptedLoadConstants(true);
            const constants = await collectionPointsService.getCollectionPointPurposeConstants();
            setPurposeConstants(constants);
            
            // Persist data to parent
            if (onDataChange) {
                onDataChange((prev: any) => ({
                    ...prev,
                    purposeConstants: constants
                }));
            }
        } catch (error) {
            console.error('Error loading purpose constants:', error);
        } finally {
            setLoadingConstants(false);
        }
    };

    // Track when both purposes and constants are loaded
    useEffect(() => {
        // Consider data loaded when both API calls have completed (even if arrays are empty)
        if (hasAttemptedLoadPurposes && hasAttemptedLoadConstants && !loadingPurposes && !loadingConstants) {
            setHasLoadedInitialData(true);
            
            // Persist data to parent
            if (onDataChange) {
                onDataChange((prev: any) => ({
                    ...prev,
                    hasLoadedInitialData: true
                }));
            }
        }
    }, [hasAttemptedLoadPurposes, hasAttemptedLoadConstants, loadingPurposes, loadingConstants, onDataChange]);

    // Function to remove a purpose mapping
    const handleRemovePurposeMapping = async (mappingId: number) => {
        if (!collectionPoint?.CollectionPointId) return;
        
        // Find the purpose mapping to get the purpose ID
        const purposeMapping = mappedPurposes.find(mapping => mapping.MappingId === mappingId);
        if (!purposeMapping) {
            setErrorMessage('Purpose mapping not found.');
            return;
        }
        
        // Show browser confirmation dialog
        const isConfirmed = window.confirm(`Are you sure you want to remove the purpose mapping for "${purposeMapping.purposeName}"? This action cannot be undone.`);
        
        if (!isConfirmed) {
            return; // User cancelled the operation
        }
        
        // Add mapping ID to deleting set to show loader
        setDeletingMappingIds(prev => new Set(prev).add(mappingId));
        
        try {
            // Call the unmapPurpose service function
            await collectionPointsService.unmapPurpose(collectionPoint.CollectionPointId, purposeMapping.PurposeId);
            
            // Show success message
            setSuccessMessage('Purpose mapping removed successfully!');
            
            // Refresh the mapped purposes list from the server to get the updated data
            // This ensures we have the real data and any server-side changes
            await loadMappedPurposes();
            
        } catch (error) {
            console.error('Error removing purpose mapping:', error);
            
            // Provide more specific error messages
            let errorMsg = 'Failed to remove purpose mapping. Please try again.';
            if (error instanceof Error) {
                if (error.message.includes('NotFoundError')) {
                    errorMsg = 'Purpose mapping not found or does not belong to this collection point.';
                } else if (error.message.includes('published')) {
                    errorMsg = 'Cannot modify a published collection point.';
                } else {
                    errorMsg = error.message;
                }
            }
            
            setErrorMessage(errorMsg);
        } finally {
            // Remove mapping ID from deleting set to hide loader
            setDeletingMappingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(mappingId);
                return newSet;
            });
        }
    };

    const loadMappedPurposes = async () => {
        if (!collectionPoint?.CollectionPointId) return;
        
        try {
            setLoadingMappedPurposes(true);
            setHasAttemptedLoadMappedPurposes(true);
            const mapped = await collectionPointsService.getMappedPurposes(collectionPoint.CollectionPointId);
            setMappedPurposes(mapped);
            
            // Persist data to parent
            if (onDataChange) {
                onDataChange((prev: any) => ({
                    ...prev,
                    mappedPurposes: mapped,
                    hasAttemptedLoadMappedPurposes: true
                }));
            }
        } catch (error) {
            console.error('Error loading mapped purposes:', error);
        } finally {
            setLoadingMappedPurposes(false);
        }
    };

    // Helper functions - moved from parent component
    const truncateText = (text: string, maxLength: number): string => {
        if (!text) return '';
        return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    };

    const getConstantByCategoryAndCode = (category: string, code: string): CollectionPointPurposeConstant | undefined => {
        return purposeConstants.find(c => c.Category === category && c.Code === code);
    };

    // Helper functions for expandable cards
    const toggleCardExpansion = (mappingId: number) => {
        const newExpandedCards = new Set(expandedCards);
        if (newExpandedCards.has(mappingId)) {
            newExpandedCards.delete(mappingId);
        } else {
            newExpandedCards.add(mappingId);
        }
        setExpandedCards(newExpandedCards);
    };

    const isCardExpanded = (mappingId: number) => {
        return expandedCards.has(mappingId);
    };

    const validateLawfulGround = (): boolean => {
        if (lawfulGroundId === '' || lawfulGroundId === 0) {
            setLawfulGroundError('Lawful Ground is required');
            setErrorMessage('Lawful Ground is required'); // Show snackbar as well
            return false;
        }
        setLawfulGroundError('');
        return true;
    };

    const validateRequiredFields = (): boolean => {
        // Validate Lawful Ground
        if (!validateLawfulGround()) {
            return false;
        }
        
        // Validate Purpose Category
        if (purposeCategoryId === '' || purposeCategoryId === 0) {
            setErrorMessage('Purpose Category is required');
            return false;
        }
        
        // Validate Retention Period Type
        if (retentionPeriodTypeId === '' || retentionPeriodTypeId === 0) {
            setErrorMessage('Retention Period Type is required');
            return false;
        }
        
        // Validate Start Date Type
        if (startDateTypeId === '' || startDateTypeId === 0) {
            setErrorMessage('Start Date Type is required');
            return false;
        }
        
        // Validate Time Based Period if retention type is time-based
        const timeBasedConstant = getConstantByCategoryAndCode('RETENTION_TYPE', 'TIME_BASED');
        if (timeBasedConstant && retentionPeriodTypeId === timeBasedConstant.ConstantId) {
            if (!timeBasedPeriod || timeBasedPeriod <= 0) {
                setTimeBasedPeriodError('Time Based Period is required when retention type is time-based');
                setErrorMessage('Time Based Period is required when retention type is time-based');
                return false;
            } else {
                setTimeBasedPeriodError('');
            }
            if (!timeBasedUnitId || timeBasedUnitId === 0) {
                setTimeBasedUnitError('Time Based Unit is required when retention type is time-based');
                setErrorMessage('Time Based Unit is required when retention type is time-based');
                return false;
            } else {
                setTimeBasedUnitError('');
            }
        } else {
            // Clear errors if not time-based
            setTimeBasedPeriodError('');
            setTimeBasedUnitError('');
        }
        
        // Validate Custom Start Date if start date type is custom
        if (isCustomDateSelected && !customStartDate) {
            setCustomStartDateError('Custom Start Date is required when start date type is custom');
            setErrorMessage('Custom Start Date is required when start date type is custom');
            return false;
        } else {
            setCustomStartDateError('');
        }
        
        // Validate Legal Retention Period if legal retention is required
        if (legalRetentionRequired && (!legalRetentionPeriod || legalRetentionPeriod <= 0)) {
            setLegalRetentionPeriodError('Legal Retention Period is required when legal retention is required');
            setErrorMessage('Legal Retention Period is required when legal retention is required');
            return false;
        } else {
            setLegalRetentionPeriodError('');
        }
        
        // Validate that at least one end condition is selected
        if (!endOnPurposeFulfilled && !endOnConsentWithdrawn && !endOnRetentionExpired && !endOnErasureRequested) {
            setEndConditionsError('At least one end condition must be selected');
            setErrorMessage('At least one end condition must be selected');
            return false;
        } else {
            setEndConditionsError('');
        }
        
        return true;
    };

    const handlePurposeSelection = (purpose: Purpose) => {
        setSelectedPurpose(purpose);
        resetPurposeMappingForm();
        
        // Set default values based on constants
        const consentConstant = getConstantByCategoryAndCode('LAWFUL_GROUND', 'CONSENT');
        const primaryConstant = getConstantByCategoryAndCode('PURPOSE_CATEGORY', 'PRIMARY');
        const timeBasedConstant = getConstantByCategoryAndCode('RETENTION_TYPE', 'TIME_BASED');
        const daysConstant = getConstantByCategoryAndCode('TIME_UNIT', 'DAYS');
        const collectionDateConstant = getConstantByCategoryAndCode('START_DATE_TYPE', 'COLLECTION_DATE');
        
        if (consentConstant) setLawfulGroundId(consentConstant.ConstantId);
        if (primaryConstant) setPurposeCategoryId(primaryConstant.ConstantId);
        if (timeBasedConstant) setRetentionPeriodTypeId(timeBasedConstant.ConstantId);
        if (daysConstant) setTimeBasedUnitId(daysConstant.ConstantId);
        if (collectionDateConstant) setStartDateTypeId(collectionDateConstant.ConstantId);
        
        // Set default time period
        setTimeBasedPeriod(30);
        
        // End conditions remain false by default (user must explicitly select)
        
        // Clear validation errors when selecting a new purpose
        setLawfulGroundError('');
        setHasAttemptedPurposeValidation(false);
        
        // Reset user changes flag since we're setting defaults
        setHasUserChanges(false);
        
        // Persist form state to parent
        setTimeout(() => {
            const currentState = {
                lawfulGroundId: consentConstant?.ConstantId || '' as number | '',
                purposeCategoryId: primaryConstant?.ConstantId || '' as number | '',
                isRequired: false,
                retentionPeriodTypeId: timeBasedConstant?.ConstantId || '' as number | '',
                timeBasedPeriod: 30 as number | '',
                timeBasedUnitId: daysConstant?.ConstantId || '' as number | '',
                startDateTypeId: collectionDateConstant?.ConstantId || '' as number | '',
                customStartDate: '',
                extensionAllowed: false,
                extensionReason: '',
                endOnPurposeFulfilled: false,
                endOnConsentWithdrawn: false,
                endOnRetentionExpired: false,
                endOnErasureRequested: false,
                legalRetentionRequired: false,
                legalRetentionPeriod: '' as number | '',
                endOnAccountClosure: false
            };
            
            if (onFormStateChange) {
                onFormStateChange({
                    selectedPurpose: purpose,
                    originalState: null,
                    currentState
                });
            }
        }, 0);
    };

    // Event handler functions - moved from parent component
    const handleAddPurposeMapping = async () => {
        if (!selectedPurpose || !collectionPoint?.CollectionPointId) return;
        
        // Set validation attempted flag
        setHasAttemptedPurposeValidation(true);
        
        // Clear any previous error messages
        setErrorMessage('');
        
        // Validate all required fields
        if (!validateRequiredFields()) {
            return; // Validation failed, error is already set
        }
        
        setIsSaving(true);
        setErrorMessage('');
        
        try {
            // Prepare the payload for the API call
            const purposePayload = {
                purposeId: selectedPurpose.purposeId,
                lawfulGroundId: lawfulGroundId as number,
                purposeCategoryId: purposeCategoryId as number,
                isRequired: isRequired,
                retentionPeriodTypeId: retentionPeriodTypeId as number,
                timeBasedPeriod: timeBasedPeriod as number || undefined,
                timeBasedUnitId: timeBasedUnitId as number || undefined,
                startDateTypeId: startDateTypeId as number,
                customStartDate: customStartDate ? new Date(customStartDate) : undefined,
                extensionAllowed: extensionAllowed,
                extensionReason: extensionReason || undefined,
                endOnPurposeFulfilled: endOnPurposeFulfilled,
                endOnConsentWithdrawn: endOnConsentWithdrawn,
                endOnRetentionExpired: endOnRetentionExpired,
                endOnErasureRequested: endOnErasureRequested,
                endOnAccountClosure: endOnAccountClosure,
                legalRetentionRequired: legalRetentionRequired,
                legalRetentionPeriod: legalRetentionPeriod as number || undefined
            };
            
            // Make the actual API call to add purpose mapping
            const response = await collectionPointsService.mapPurpose(collectionPoint.CollectionPointId, purposePayload);
            
            
            // Reset form after successful addition
            resetPurposeMappingForm();
            setSelectedPurpose(null);
            
            // Clear any persisted form state to prevent restoration
            if (onFormStateChange) {
                onFormStateChange(null);
            }
            
            // Reset user changes flag
            setHasUserChanges(false);
            
            // Show success message
            setSuccessMessage('Purpose mapping added successfully!');
            
            // Refresh the mapped purposes list from the server to get the updated data
            // This ensures we have the real MappingId and all server-side data
            await loadMappedPurposes();
            
            // Note: We don't call onPurposesChanged here because it would trigger
            // a full collection point refresh, which causes tab switching back to Details.
            // The local refresh via loadMappedPurposes() is sufficient.
            
        } catch (error) {
            console.error('Error adding purpose mapping:', error);
            
            // Provide more specific error messages
            let errorMsg = 'Failed to add purpose mapping. Please try again.';
            if (error instanceof Error) {
                if (error.message.includes('ValidationError')) {
                    errorMsg = 'Validation error: Please check your input values.';
                } else if (error.message.includes('NotFoundError')) {
                    errorMsg = 'Collection point not found or does not belong to your client.';
                } else if (error.message.includes('already mapped')) {
                    errorMsg = 'This purpose is already mapped to this collection point.';
                } else if (error.message.includes('published')) {
                    errorMsg = 'Cannot modify a published collection point.';
                } else {
                    errorMsg = error.message;
                }
            }
            
            setErrorMessage(errorMsg);
        } finally {
            setIsSaving(false);
        }
    };

    // useEffect hooks - moved from parent component
    useEffect(() => {
        
        
        if (collectionPoint?.CollectionPointId) {
            const currentCollectionPointId = collectionPoint.CollectionPointId;
            
            // Check if this is actually a different collection point
            if (lastCollectionPointId !== currentCollectionPointId) {
                
                // This is a new collection point, reset all state
                setMappedPurposes([]);
                setHasAttemptedLoadMappedPurposes(false);
                setHasLoadedInitialData(false);
                setHasUserChanges(false);
                setHasAttemptedLoadPurposes(false);
                setHasAttemptedLoadConstants(false);
                setLastCollectionPointId(currentCollectionPointId);
                
                // Clear all data when collection point changes
                setPurposes([]);
                setPurposeConstants([]);
                setMappedPurposes([]);
                setAvailablePurposes([]);
                setSelectedPurpose(null);
                
                // Reset form state
                resetPurposeMappingForm();
            }
        }
        
        // Load purposes and constants only if not already loaded
        if (!hasLoadedInitialData && !hasAttemptedLoadPurposes && !loadingPurposes) {
            loadPurposes();
        }
        if (!hasLoadedInitialData && !hasAttemptedLoadConstants && !loadingConstants) {
            loadPurposeConstants();
        }
    }, [collectionPoint?.CollectionPointId, hasLoadedInitialData, purposes.length, purposeConstants.length, loadingPurposes, loadingConstants, lastCollectionPointId]);

    // Load mapped purposes when collection point is available and data is ready
    useEffect(() => {
        
        
        // Only load if we have a collection point and haven't attempted to load yet
        if (collectionPoint?.CollectionPointId && 
            !hasAttemptedLoadMappedPurposes && 
            !loadingMappedPurposes) {
            loadMappedPurposes();
        }
    }, [collectionPoint?.CollectionPointId, hasAttemptedLoadMappedPurposes, loadingMappedPurposes]);

    // Restore persisted form state when component mounts
    useEffect(() => {
        if (persistedFormState && !selectedPurpose) {
            setSelectedPurpose(persistedFormState.selectedPurpose);
            
            // Restore current form state
            const currentState = persistedFormState.currentState;
            setLawfulGroundId(currentState.lawfulGroundId);
            setPurposeCategoryId(currentState.purposeCategoryId);
            setIsRequired(currentState.isRequired);
            setRetentionPeriodTypeId(currentState.retentionPeriodTypeId);
            setTimeBasedPeriod(currentState.timeBasedPeriod);
            setTimeBasedUnitId(currentState.timeBasedUnitId);
            setStartDateTypeId(currentState.startDateTypeId);
            setCustomStartDate(currentState.customStartDate);
            setExtensionAllowed(currentState.extensionAllowed);
            setExtensionReason(currentState.extensionReason);
            setEndOnPurposeFulfilled(currentState.endOnPurposeFulfilled);
            setEndOnConsentWithdrawn(currentState.endOnConsentWithdrawn);
            setEndOnRetentionExpired(currentState.endOnRetentionExpired);
            setEndOnErasureRequested(currentState.endOnErasureRequested);
            setLegalRetentionRequired(currentState.legalRetentionRequired);
            setLegalRetentionPeriod(currentState.legalRetentionPeriod);
            setEndOnAccountClosure(currentState.endOnAccountClosure);
        }
    }, [persistedFormState, selectedPurpose]);

    // Check for changes and notify parent
    useEffect(() => {
        // Since this is add-only, always report no changes to parent
        if (onHasChanges) {
            onHasChanges(false);
        }
        
        // Persist current form state to parent only when a purpose is selected
        if (onFormStateChange && selectedPurpose) {
            const currentState = {
                lawfulGroundId,
                purposeCategoryId,
                isRequired,
                retentionPeriodTypeId,
                timeBasedPeriod,
                timeBasedUnitId,
                startDateTypeId,
                customStartDate,
                extensionAllowed,
                extensionReason,
                endOnPurposeFulfilled,
                endOnConsentWithdrawn,
                endOnRetentionExpired,
                endOnErasureRequested,
                legalRetentionRequired,
                legalRetentionPeriod,
                endOnAccountClosure
            };
            onFormStateChange({
                selectedPurpose,
                originalState: null,
                currentState
            });
        } else if (onFormStateChange && !selectedPurpose) {
            // Clear persisted form state when no purpose is selected
            onFormStateChange(null);
        }
    }, [
        lawfulGroundId, purposeCategoryId, isRequired, retentionPeriodTypeId,
        timeBasedPeriod, timeBasedUnitId, startDateTypeId, customStartDate,
        extensionAllowed, extensionReason, endOnPurposeFulfilled, endOnConsentWithdrawn,
        endOnRetentionExpired, endOnErasureRequested, legalRetentionRequired,
        legalRetentionPeriod, endOnAccountClosure, onHasChanges, onFormStateChange,
        selectedPurpose
    ]);

    // Track user changes for tab indicator
    useEffect(() => {
        if (selectedPurpose) {
            // Check if current state differs from default state
            const hasChanges = (
                lawfulGroundId !== getConstantByCategoryAndCode('LAWFUL_GROUND', 'CONSENT')?.ConstantId ||
                purposeCategoryId !== getConstantByCategoryAndCode('PURPOSE_CATEGORY', 'PRIMARY')?.ConstantId ||
                isRequired !== false ||
                retentionPeriodTypeId !== getConstantByCategoryAndCode('RETENTION_TYPE', 'TIME_BASED')?.ConstantId ||
                timeBasedPeriod !== 30 ||
                timeBasedUnitId !== getConstantByCategoryAndCode('TIME_UNIT', 'DAYS')?.ConstantId ||
                startDateTypeId !== getConstantByCategoryAndCode('START_DATE_TYPE', 'COLLECTION_DATE')?.ConstantId ||
                customStartDate !== '' ||
                extensionAllowed !== false ||
                extensionReason !== '' ||
                endOnPurposeFulfilled !== false ||
                endOnConsentWithdrawn !== false ||
                endOnRetentionExpired !== false ||
                endOnErasureRequested !== false ||
                legalRetentionRequired !== false ||
                legalRetentionPeriod !== '' ||
                endOnAccountClosure !== false
            );
            setHasUserChanges(hasChanges);
            
            // Notify parent of user changes for tab indicator
            if (onUserChanges) {
                onUserChanges(hasChanges);
            }
        } else {
            setHasUserChanges(false);
            if (onUserChanges) {
                onUserChanges(false);
            }
        }
    }, [
        selectedPurpose, lawfulGroundId, purposeCategoryId, isRequired, retentionPeriodTypeId,
        timeBasedPeriod, timeBasedUnitId, startDateTypeId, customStartDate,
        extensionAllowed, extensionReason, endOnPurposeFulfilled, endOnConsentWithdrawn,
        endOnRetentionExpired, endOnErasureRequested, legalRetentionRequired,
        legalRetentionPeriod, endOnAccountClosure, onUserChanges
    ]);

    // Calculate available purposes (purposes not already mapped)
    useEffect(() => {
        if (purposes.length > 0 && mappedPurposes.length >= 0) {
            const mappedPurposeIds = mappedPurposes.map(mp => mp.PurposeId);
            const available = purposes.filter(purpose => !mappedPurposeIds.includes(purpose.purposeId));
            setAvailablePurposes(available);
        }
    }, [purposes, mappedPurposes]);



    // Component will be implemented in subsequent steps
    return (
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                Purposes Configuration
            </Typography>
            
            {/* Loading states */}
            {(loadingPurposes || loadingConstants || loadingMappedPurposes) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <CircularProgress size={20} />
                    <Typography color="text.primary">Loading purposes configuration...</Typography>
                </Box>
            )}

            {/* Success/Error Messages */}
            <Snackbar
                open={!!successMessage}
                autoHideDuration={config.ui.snackbar.autoHideDuration}
                anchorOrigin={config.ui.snackbar.anchorOrigin}
                onClose={() => setSuccessMessage('')}
                message={successMessage}
            />
            <Snackbar
                key={errorMessage} // Force re-render when message changes
                open={!!errorMessage}
                autoHideDuration={config.ui.snackbar.autoHideDuration}
                anchorOrigin={config.ui.snackbar.anchorOrigin}
                onClose={() => setErrorMessage('')}
                message={errorMessage}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        bgcolor: theme.palette.error.main,
                    },
                }}
            />



            {/* Placeholder for the complex 3-column layout */}
            {!loadingPurposes && !loadingConstants && !loadingMappedPurposes && (
                <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    height: 'calc(100% - 80px)', 
                    minHeight: 500,
                    width: '100%',
                }}>
                    {/* Left Column - Available Purposes */}
                    <Box sx={{ 
                        flex: '0 0 20%',
                        minWidth: 150,
                        maxWidth: 350,
                        bgcolor: theme.palette.background.paper,
                        borderRadius: theme.shape.borderRadius,
                        border: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        <Box sx={{ 
                            p: 2, 
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            bgcolor: theme.palette.background.default
                        }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                Available Purposes
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {availablePurposes.length} purposes available
                            </Typography>
                        </Box>
                        <Box sx={{ 
                            flex: 1, 
                            overflow: 'auto', 
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2
                        }}>
                            {availablePurposes.length === 0 ? (
                                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                    No available purposes to map
                                </Typography>
                            ) : (
                                availablePurposes.map((purpose) => {
                                    const nameTruncated = truncateText(purpose.name, 32);
                                    const deptTruncated = truncateText(purpose.departmentName || '', 24);
                                    const descTruncated = truncateText(purpose.description || '', 60);
                                    return (
                                        <Card
                                            key={purpose.purposeId}
                                            variant={selectedPurpose?.purposeId === purpose.purposeId ? 'elevation' : 'outlined'}
                                            sx={{
                                                cursor: 'pointer',
                                                boxShadow: selectedPurpose?.purposeId === purpose.purposeId ? 6 : undefined,
                                                borderColor: selectedPurpose?.purposeId === purpose.purposeId ? theme.palette.primary.main : theme.palette.divider,
                                                bgcolor: selectedPurpose?.purposeId === purpose.purposeId ? theme.palette.action.selected : theme.palette.background.paper,
                                                transition: 'box-shadow 0.2s, border-color 0.2s, background 0.2s',
                                                '&:hover': {
                                                    borderColor: theme.palette.primary.main,
                                                    boxShadow: 4,
                                                },
                                                minHeight: 80,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                px: 2,
                                                py: 1.5,
                                            }}
                                            onClick={() => handlePurposeSelection(purpose)}
                                        >
                                            {/* Name with tooltip if truncated */}
                                            {purpose.name.length > 32 ? (
                                                <Tooltip title={purpose.name} placement="top" arrow>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, cursor: 'help' }}>
                                                        {nameTruncated}
                                                    </Typography>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    {nameTruncated}
                                                </Typography>
                                            )}
                                            {/* Department with tooltip if truncated */}
                                            {(purpose.departmentName && purpose.departmentName.length > 24) ? (
                                                <Tooltip title={purpose.departmentName} placement="top" arrow>
                                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, cursor: 'help' }}>
                                                        {deptTruncated}
                                                    </Typography>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                                                    {deptTruncated}
                                                </Typography>
                                            )}
                                            {/* Description with tooltip if truncated */}
                                            {(purpose.description && purpose.description.length > 60) ? (
                                                <Tooltip title={purpose.description} placement="top" arrow>
                                                    <Typography variant="body2" color="text.secondary" sx={{ cursor: 'help' }}>
                                                        {descTruncated}
                                                    </Typography>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    {descTruncated}
                                                </Typography>
                                            )}
                                        </Card>
                                    );
                                })
                            )}
                        </Box>
                    </Box>

                    {/* Center Column - Mapping Configuration */}
                    <Box sx={{ 
                        flex: '0 0 50%',
                        minWidth: 300,
                        maxWidth: 'none',
                        bgcolor: theme.palette.background.paper,
                        borderRadius: theme.shape.borderRadius,
                        border: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        <Box sx={{ 
                            p: 2, 
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            bgcolor: theme.palette.background.default
                        }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                Purpose Mapping Configuration
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {selectedPurpose ? `Configuring: ${selectedPurpose.name}` : 'Select a purpose to configure mapping'}
                            </Typography>
                        </Box>
                        <Box sx={{ 
                            flex: 1, 
                            overflow: 'auto', 
                            p: 1
                        }}>
                            <>
                                {!selectedPurpose ? (
                                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                        Select a purpose from the left column to configure mapping
                                    </Typography>
                                ) : (
                                    <Box sx={{ width: '100%' }}>
                                        <Stack spacing={2.5}>
                                            {/* Card 1: Basic Configuration */}
                                            <Card sx={{ p: 4, bgcolor: theme.palette.background.paper }}>
                                                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                                                    Basic Configuration
                                                </Typography>
                                                <Stack spacing={3}>
                                                    {/* Lawful Ground */}
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Lawful Ground *</Typography>
                                                        <select
                                                            value={lawfulGroundId || ''}
                                                            onChange={(e) => setLawfulGroundId(e.target.value ? Number(e.target.value) : '')}
                                                            disabled={isPublished}
                                                            style={{
                                                                width: '100%',
                                                                padding: '8.5px 14px',
                                                                border: `1px solid ${hasAttemptedPurposeValidation && lawfulGroundError ? theme.palette.error.main : theme.palette.divider}`,
                                                                borderRadius: theme.shape.borderRadius,
                                                                backgroundColor: theme.palette.background.paper,
                                                                color: theme.palette.text.primary,
                                                                fontSize: '0.875rem',
                                                                fontFamily: theme.typography.fontFamily,
                                                            }}
                                                        >
                                                            <option value="">Select Lawful Ground</option>
                                                            {purposeConstants
                                                                .filter(constant => constant.Category === 'LAWFUL_GROUND')
                                                                .map(constant => (
                                                                    <option key={constant.ConstantId} value={constant.ConstantId}>
                                                                        {constant.Label}
                                                                    </option>
                                                                ))}
                                                        </select>
                                                        {hasAttemptedPurposeValidation && lawfulGroundError && (
                                                            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                                                                {lawfulGroundError}
                                                            </Typography>
                                                        )}
                                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                                                            {(() => {
                                                                const selected = purposeConstants.find(
                                                                    c => c.Category === 'LAWFUL_GROUND' && c.ConstantId === lawfulGroundId
                                                                );
                                                                return selected?.Description || '';
                                                            })()}
                                                        </Typography>
                                                    </Box>
                                                    {/* Purpose Category and Is Required */}
                                                    <Stack direction="row" spacing={3} alignItems="flex-start" sx={{ mt: 2, mb: 1 }}>
                                                        <Box sx={{ flex: 1 }}>
                                                            <FormControl component="fieldset" sx={{ width: '100%' }}>
                                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Purpose Category *</Typography>
                                                                <Stack direction="row" spacing={2}>
                                                                    {purposeConstants
                                                                        .filter(constant => constant.Category === 'PURPOSE_CATEGORY')
                                                                        .map(constant => (
                                                                            <FormControlLabel
                                                                                key={constant.ConstantId}
                                                                                control={
                                                                                    <Radio 
                                                                                        checked={purposeCategoryId === constant.ConstantId}
                                                                                        onChange={() => setPurposeCategoryId(constant.ConstantId)}
                                                                                        disabled={isPublished}
                                                                                    />
                                                                                }
                                                                                label={constant.Label}
                                                                            />
                                                                        ))}
                                                                </Stack>
                                                            </FormControl>
                                                        </Box>
                                                        <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                                                        <Box sx={{ minWidth: 180, pl: 1 }}>
                                                            <FormControlLabel
                                                                control={<Switch checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} disabled={isPublished} />}
                                                                label="Is Required"
                                                            />
                                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                                {(() => {
                                                                    const selected = purposeConstants.find(
                                                                        c => c.Category === 'LAWFUL_GROUND' && c.ConstantId === lawfulGroundId
                                                                    );
                                                                    if (selected && selected.Code === 'CONSENT') {
                                                                        return 'The user must explicitly consent to this purpose if Lawful Ground is Consent. If not, the consent record will be rejected.';
                                                                    } else {
                                                                        return 'This setting is only enforced when the lawful ground is consent.';
                                                                    }
                                                                })()}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Stack>
                                            </Card>

                                            {/* Card 2: Retention Settings */}
                                            <Card sx={{ p: 3, bgcolor: theme.palette.background.paper }}>
                                                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                                                    Retention Settings
                                                </Typography>
                                                <Stack spacing={3}>
                                                    {/* Retention Period Type */}
                                                    <FormControl component="fieldset">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                            <Typography variant="subtitle2">Retention Period Type *</Typography>
                                                            <Tooltip
                                                                title={
                                                                    `As per the DPDP Act, personal data must be retained only as long as necessary. 
- Time-based: Data is deleted after a fixed period (e.g., 30 days).
- Event-based: Data is deleted when a specific event occurs (e.g., purpose fulfilled, consent withdrawn).
- Hybrid: Data is deleted at the earliest of a fixed period or an event. 
Delete data promptly when the retention condition is met.`
                                                                }
                                                                placement="top"
                                                                arrow
                                                                sx={{
                                                                    '& .MuiTooltip-tooltip': {
                                                                        maxWidth: 400,
                                                                        fontSize: '0.875rem',
                                                                        bgcolor: theme.palette.grey[900],
                                                                        color: theme.palette.common.white,
                                                                    }
                                                                }}
                                                            >
                                                                <InfoIcon sx={{ fontSize: 18, color: theme.palette.text.secondary, cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <Stack direction="row" spacing={2}>
                                                            {purposeConstants
                                                                .filter(constant => constant.Category === 'RETENTION_TYPE')
                                                                .map(constant => (
                                                                    <Box key={constant.ConstantId}>
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Radio 
                                                                                    checked={retentionPeriodTypeId === constant.ConstantId}
                                                                                    onChange={() => setRetentionPeriodTypeId(constant.ConstantId)}
                                                                                    disabled={isPublished}
                                                                                />
                                                                            }
                                                                            label={constant.Label}
                                                                        />
                                                                        {constant.Description && (
                                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                                                {constant.Description}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                ))}
                                                        </Stack>
                                                    </FormControl>

                                                    {/* Time Based Period Fields */}
                                                    <Box sx={{ pl: 2 }}>
                                                        <Grid container spacing={2} alignItems="center">
                                                            <Grid item xs={6}>
                                                                <TextField
                                                                    label="Period"
                                                                    type="number"
                                                                    size="small"
                                                                    value={timeBasedPeriod || ''}
                                                                    onChange={(e) => setTimeBasedPeriod(e.target.value ? Number(e.target.value) : '')}
                                                                    disabled={isPublished}
                                                                    fullWidth
                                                                    error={!!timeBasedPeriodError}
                                                                    helperText={timeBasedPeriodError}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                {/* Replace MUI Select with HTML select for Unit */}
                                                                <select
                                                                    value={timeBasedUnitId || (() => {
                                                                        // Default to 'DAYS' if not set
                                                                        const daysConstant = purposeConstants.find(c => c.Category === 'TIME_UNIT' && c.Code === 'DAYS');
                                                                        return daysConstant ? daysConstant.ConstantId : '';
                                                                    })()}
                                                                    onChange={e => setTimeBasedUnitId(Number(e.target.value))}
                                                                    disabled={isPublished}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '8.5px 14px',
                                                                        borderRadius: 4,
                                                                        border: `1px solid ${timeBasedUnitError ? theme.palette.error.main : theme.palette.divider}`,
                                                                        background: theme.palette.background.paper,
                                                                        color: theme.palette.text.primary,
                                                                        fontSize: '1rem',
                                                                        fontFamily: theme.typography.fontFamily,
                                                                    }}
                                                                >
                                                                    {purposeConstants
                                                                        .filter(constant => constant.Category === 'TIME_UNIT')
                                                                        .map(constant => (
                                                                            <option key={constant.ConstantId} value={constant.ConstantId}>
                                                                                {constant.Label}
                                                                            </option>
                                                                        ))}
                                                                </select>
                                                                {timeBasedUnitError && (
                                                                    <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                                                                        {timeBasedUnitError}
                                                                    </Typography>
                                                                )}
                                                            </Grid>
                                                        </Grid>
                                                    </Box>

                                                    {/* Start Date Type */}
                                                    <FormControl component="fieldset">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                            <Typography variant="subtitle2">Start Date Type *</Typography>
                                                            <Tooltip
                                                                title={
                                                                    'The start date type determines when the retention period begins (e.g., from data collection, account creation, or a custom date). As per the DPDP Act, organizations must clearly define and document the start of the retention period to ensure compliance and transparency.'
                                                                }
                                                                placement="top"
                                                                arrow
                                                                sx={{
                                                                    '& .MuiTooltip-tooltip': {
                                                                        maxWidth: 400,
                                                                        fontSize: '0.875rem',
                                                                        bgcolor: theme.palette.grey[900],
                                                                        color: theme.palette.common.white,
                                                                    }
                                                                }}
                                                            >
                                                                <InfoIcon sx={{ fontSize: 18, color: theme.palette.text.secondary, cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <Stack direction="row" spacing={2}>
                                                            {purposeConstants
                                                                .filter(constant => constant.Category === 'START_DATE_TYPE')
                                                                .map(constant => (
                                                                    <FormControlLabel
                                                                        key={constant.ConstantId}
                                                                        control={
                                                                            <Radio 
                                                                                checked={startDateTypeId === constant.ConstantId}
                                                                                onChange={() => setStartDateTypeId(constant.ConstantId)}
                                                                                disabled={isPublished}
                                                                            />
                                                                        }
                                                                        label={constant.Label}
                                                                    />
                                                                ))}
                                                        </Stack>
                                                    </FormControl>

                                                    {/* Custom Start Date - only show if selected type is CUSTOM_DATE */}
                                                    <Box sx={{ pl: 2 }}>
                                                        <TextField
                                                            label="Custom Start Date"
                                                            type="date"
                                                            size="small"
                                                            value={customStartDate}
                                                            onChange={(e) => setCustomStartDate(e.target.value)}
                                                            disabled={isPublished || !isCustomDateSelected}
                                                            fullWidth
                                                            error={!!customStartDateError}
                                                            helperText={customStartDateError}
                                                            InputLabelProps={{ shrink: true }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    bgcolor: theme.palette.background.paper,
                                                                    '& fieldset': {
                                                                        borderColor: theme.palette.divider,
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: theme.palette.primary.main,
                                                                    },
                                                                    '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                                                        filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                                                                    },
                                                                },
                                                                '& .MuiInputLabel-root': {
                                                                    color: theme.palette.text.secondary,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                </Stack>
                                            </Card>

                                            {/* Card 4: End Conditions */}
                                            <Card sx={{ p: 3, bgcolor: theme.palette.background.paper }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                    <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                                                        End Conditions *
                                                    </Typography>
                                                    <Tooltip
                                                        title={
                                                            'This section allows fine-grained control over which specific events (e.g., purpose fulfilled, consent withdrawn, erasure requested, account closure) will trigger data deletion for this mapping. Use these switches to customize retention behavior beyond the main retention settings. At least one end condition must be selected.'
                                                        }
                                                        placement="top"
                                                        arrow
                                                        sx={{
                                                            '& .MuiTooltip-tooltip': {
                                                                maxWidth: 400,
                                                                fontSize: '0.875rem',
                                                                bgcolor: theme.palette.grey[900],
                                                                color: theme.palette.common.white,
                                                            }
                                                        }}
                                                    >
                                                        <InfoIcon sx={{ fontSize: 18, color: theme.palette.text.secondary, cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }} />
                                                    </Tooltip>
                                                </Box>
                                                {endConditionsError && (
                                                    <Typography color="error" variant="caption" sx={{ mb: 2, display: 'block' }}>
                                                        {endConditionsError}
                                                    </Typography>
                                                )}
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <FormControlLabel
                                                            control={<Switch checked={endOnPurposeFulfilled} onChange={(e) => setEndOnPurposeFulfilled(e.target.checked)} disabled={isPublished} />}
                                                            label="Purpose Fulfilled"
                                                        />
                                                            <Tooltip
                                                                title="As per Section 8(7) of the DPDP Act, personal data must be deleted when the purpose for which it was collected has been fulfilled. This end condition triggers data deletion when the specific purpose for which consent was given has been completed or achieved. For example, if data was collected for a one-time service request, deletion occurs once the service is delivered."
                                                                placement="top"
                                                                arrow
                                                                sx={{
                                                                    '& .MuiTooltip-tooltip': {
                                                                        maxWidth: 400,
                                                                        fontSize: '0.875rem',
                                                                        bgcolor: theme.palette.grey[900],
                                                                        color: theme.palette.common.white,
                                                                    }
                                                                }}
                                                            >
                                                                <InfoIcon sx={{ fontSize: 16, color: theme.palette.text.secondary, cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }} />
                                                            </Tooltip>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <FormControlLabel
                                                            control={<Switch checked={endOnConsentWithdrawn} onChange={(e) => setEndOnConsentWithdrawn(e.target.checked)} disabled={isPublished} />}
                                                            label="Consent Withdrawn"
                                                        />
                                                            <Tooltip
                                                                title="As per Section 6(6) of the DPDP Act, data principals have the right to withdraw consent at any time. When consent is withdrawn, the data fiduciary must cease processing and delete the personal data within a reasonable time. This end condition ensures immediate data deletion when a data principal withdraws their consent for the specific purpose."
                                                                placement="top"
                                                                arrow
                                                                sx={{
                                                                    '& .MuiTooltip-tooltip': {
                                                                        maxWidth: 400,
                                                                        fontSize: '0.875rem',
                                                                        bgcolor: theme.palette.grey[900],
                                                                        color: theme.palette.common.white,
                                                                    }
                                                                }}
                                                            >
                                                                <InfoIcon sx={{ fontSize: 16, color: theme.palette.text.secondary, cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }} />
                                                            </Tooltip>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <FormControlLabel
                                                            control={<Switch checked={endOnRetentionExpired} onChange={(e) => setEndOnRetentionExpired(e.target.checked)} disabled={isPublished} />}
                                                            label="Retention Expired"
                                                        />
                                                            <Tooltip
                                                                title="As per Section 8(7) of the DPDP Act, personal data must not be retained beyond the period necessary to satisfy the purpose for which it was collected. This end condition triggers data deletion when the specified retention period (time-based or event-based) has expired. The retention period must be reasonable and proportionate to the purpose."
                                                                placement="top"
                                                                arrow
                                                                sx={{
                                                                    '& .MuiTooltip-tooltip': {
                                                                        maxWidth: 400,
                                                                        fontSize: '0.875rem',
                                                                        bgcolor: theme.palette.grey[900],
                                                                        color: theme.palette.common.white,
                                                                    }
                                                                }}
                                                            >
                                                                <InfoIcon sx={{ fontSize: 16, color: theme.palette.text.secondary, cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }} />
                                                            </Tooltip>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <FormControlLabel
                                                            control={<Switch checked={endOnErasureRequested} onChange={(e) => setEndOnErasureRequested(e.target.checked)} disabled={isPublished} />}
                                                            label="Erasure Requested"
                                                        />
                                                            <Tooltip
                                                                title="As per Section 12(1)(a) of the DPDP Act, data principals have the right to correction and erasure of their personal data. When a data principal requests erasure of their data, the data fiduciary must delete the personal data within a reasonable time. This end condition ensures compliance with erasure requests and protects data principal rights."
                                                                placement="top"
                                                                arrow
                                                                sx={{
                                                                    '& .MuiTooltip-tooltip': {
                                                                        maxWidth: 400,
                                                                        fontSize: '0.875rem',
                                                                        bgcolor: theme.palette.grey[900],
                                                                        color: theme.palette.common.white,
                                                                    }
                                                                }}
                                                            >
                                                                <InfoIcon sx={{ fontSize: 16, color: theme.palette.text.secondary, cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }} />
                                                            </Tooltip>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <FormControlLabel
                                                            control={<Switch checked={endOnAccountClosure} onChange={(e) => setEndOnAccountClosure(e.target.checked)} disabled={isPublished} />}
                                                            label="Account Closure"
                                                        />
                                                            <Tooltip
                                                                title="When a data principal closes their account or terminates their relationship with the data fiduciary, personal data should be deleted unless there are legitimate legal or regulatory requirements for retention. This end condition ensures data deletion upon account closure, protecting data principals from unnecessary data retention and potential privacy risks."
                                                                placement="top"
                                                                arrow
                                                                sx={{
                                                                    '& .MuiTooltip-tooltip': {
                                                                        maxWidth: 400,
                                                                        fontSize: '0.875rem',
                                                                        bgcolor: theme.palette.grey[900],
                                                                        color: theme.palette.common.white,
                                                                    }
                                                                }}
                                                            >
                                                                <InfoIcon sx={{ fontSize: 16, color: theme.palette.text.secondary, cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }} />
                                                            </Tooltip>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Card>

                                            {/* Card 3: Extension Settings */}
                                            <Card sx={{ p: 3, bgcolor: theme.palette.background.paper }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                    <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                                                        Extension Settings
                                                    </Typography>
                                                    <Tooltip
                                                        title={
                                                            'As per the DPDP Act, extension of data retention beyond the original period is permitted only if legally justified. Any extension must be documented, limited to the minimum necessary duration, and supported by a valid legal or regulatory requirement.'
                                                        }
                                                        placement="top"
                                                        arrow
                                                        sx={{
                                                            '& .MuiTooltip-tooltip': {
                                                                maxWidth: 400,
                                                                fontSize: '0.875rem',
                                                                bgcolor: theme.palette.grey[900],
                                                                color: theme.palette.common.white,
                                                            }
                                                        }}
                                                    >
                                                        <InfoIcon sx={{ fontSize: 18, color: theme.palette.text.secondary, cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }} />
                                                    </Tooltip>
                                                </Box>
                                                <Stack spacing={3}>
                                                    {/* Extension Allowed */}
                                                    <FormControlLabel
                                                        control={<Switch checked={extensionAllowed} onChange={(e) => setExtensionAllowed(e.target.checked)} disabled={isPublished} />}
                                                        label="Extension Allowed"
                                                    />
                                                </Stack>
                                            </Card>

                                            <Card sx={{ 
                                                p: 3, 
                                                border: `1px solid ${theme.palette.info.main}`, 
                                                bgcolor: theme.palette.mode === 'dark' ? theme.palette.info.dark + '20' : theme.palette.info.light + '20', 
                                                mb: 2 
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                    <Typography variant="h6" sx={{ color: theme.palette.info.main, fontWeight: 500 }}>
                                                        Legal Retention Policy
                                                    </Typography>
                                                    <Tooltip
                                                        title={
                                                            'Legal retention is required only when a law or regulation mandates data retention for a specific period (e.g., tax, audit, law enforcement). Consult your legal team before enabling this. As per the DPDP Act, data must not be retained longer than necessary, except where legally required.'
                                                        }
                                                        placement="top"
                                                        arrow
                                                        sx={{
                                                            '& .MuiTooltip-tooltip': {
                                                                maxWidth: 400,
                                                                fontSize: '0.875rem',
                                                                bgcolor: theme.palette.grey[900],
                                                                color: theme.palette.common.white,
                                                            }
                                                        }}
                                                    >
                                                        <InfoIcon sx={{ fontSize: 20, color: theme.palette.info.main, cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }} />
                                                    </Tooltip>
                                                </Box>
                                                <Stack spacing={3}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={legalRetentionRequired}
                                                                onChange={e => setLegalRetentionRequired(e.target.checked)}
                                                                disabled={isPublished}
                                                            />
                                                        }
                                                        label="Legal Retention Required"
                                                    />
                                                    {legalRetentionRequired && (
                                                        <TextField
                                                            label="Legal Retention Period (days)"
                                                            type="number"
                                                            size="small"
                                                            value={legalRetentionPeriod}
                                                            onChange={e => setLegalRetentionPeriod(e.target.value ? Number(e.target.value) : '')}
                                                            disabled={isPublished}
                                                            fullWidth
                                                            error={!!legalRetentionPeriodError}
                                                            helperText={legalRetentionPeriodError}
                                                        />
                                                    )}
                                                    <Typography variant="caption" color="text.secondary">
                                                        If legal retention is required, specify the number of days for which data must be retained as per law. Data will not be deleted before this period, even if other end conditions are met.
                                                    </Typography>
                                                </Stack>
                                            </Card>

                                            {selectedPurpose && !isPublished && (
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        disabled={isSaving}
                                                        onClick={handleAddPurposeMapping}
                                                        sx={{
                                                            bgcolor: theme.palette.primary.main,
                                                            color: theme.palette.primary.contrastText,
                                                            '&:hover': {
                                                                bgcolor: theme.palette.primary.dark,
                                                            },
                                                            '&.Mui-disabled': {
                                                                bgcolor: theme.palette.action.disabledBackground,
                                                                color: theme.palette.action.disabled,
                                                            },
                                                            minWidth: 100,
                                                        }}
                                                    >
                                                        {isSaving ? (
                                                            <CircularProgress size={20} color="inherit" />
                                                        ) : (
                                                            'Add Purpose'
                                                        )}
                                                    </Button>
                                                </Box>
                                            )}
                                        </Stack>
                                    </Box>
                                )}
                            </>
                        </Box>
                    </Box>

                    {/* Right Column - Mapped Purposes */}
                    <Box sx={{ 
                        flex: '0 0 30%',
                        minWidth: 150,
                        maxWidth: 400,
                        bgcolor: theme.palette.background.paper,
                        borderRadius: theme.shape.borderRadius,
                        border: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        mr: 2,
                    }}>
                        <Box sx={{ 
                            p: 2, 
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            bgcolor: theme.palette.background.default
                        }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                Mapped Purposes
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {mappedPurposes.length} purposes mapped
                            </Typography>
                        </Box>
                        <Box sx={{ 
                            flex: 1, 
                            overflow: 'auto', 
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2
                        }}>
                            {mappedPurposes.length === 0 ? (
                                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4, fontStyle: 'italic' }}>
                                    No purposes mapped to this collection point
                                </Typography>
                            ) : (
                                mappedPurposes.map((mappedPurpose) => {
                                    return (
                                        <Card
                                            key={mappedPurpose.MappingId}
                                            sx={{
                                                bgcolor: theme.palette.background.paper,
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: theme.shape.borderRadius,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                transition: 'all 0.2s ease-in-out',
                                                '&:hover': {
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                    borderColor: theme.palette.primary.main,
                                                },
                                                position: 'relative',
                                                overflow: 'visible',
                                            }}
                                        >
                                            <CardContent sx={{ p: 2, pb: '16px !important', position: 'relative' }}>
                                                {/* Header with purpose name */}
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    alignItems: 'flex-start',
                                                    mb: 1
                                                }}>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography 
                                                            variant="subtitle2" 
                                                            sx={{ 
                                                                fontWeight: 600,
                                                                color: theme.palette.text.primary,
                                                                mb: 0.5,
                                                                lineHeight: 1.3
                                                            }}
                                                        >
                                                            {mappedPurpose.purposeName}
                                </Typography>
                                                        <Typography 
                                                            variant="caption" 
                                                            color="text.secondary"
                                                            sx={{ 
                                                                display: 'block',
                                                                lineHeight: 1.4
                                                            }}
                                                        >
                                                            {mappedPurpose.lawfulGroundLabel}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* Quick info chips */}
                                                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                                    <Chip
                                                        label={mappedPurpose.purposeCategoryLabel}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            height: 20,
                                                            bgcolor: theme.palette.background.default,
                                                            borderColor: theme.palette.divider,
                                                        }}
                                                    />
                                                    {mappedPurpose.IsRequired && (
                                                        <Chip
                                                            label="Required"
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                height: 20,
                                                            }}
                                                        />
                                                    )}
                                                    <Chip
                                                        label={mappedPurpose.retentionPeriodTypeLabel}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            height: 20,
                                                            bgcolor: theme.palette.background.default,
                                                            borderColor: theme.palette.divider,
                                                        }}
                                                    />
                        </Box>

                                                {/* Associated items indicator */}
                                                {(mappedPurpose.dataElements.length > 0 || mappedPurpose.processors.length > 0 || mappedPurpose.preferences.length > 0) && (
                                                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                                                        {mappedPurpose.dataElements.length > 0 && (
                                                            <Tooltip 
                                                                title={
                                                                    <Box>
                                                                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                                                            Data Elements:
                                                                        </Typography>
                                                                        {mappedPurpose.dataElements.map((element, index) => (
                                                                            <Typography key={element.dataElementId} variant="caption" sx={{ display: 'block' }}>
                                                                                • {element.elementName}
                                                                            </Typography>
                                                                        ))}
                    </Box>
                                                                }
                                                                placement="top"
                                                                arrow
                                                            >
                                                                <Chip
                                                                    label={`${mappedPurpose.dataElements.length} DE`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{
                                                                        fontSize: '0.65rem',
                                                                        height: 18,
                                                                        bgcolor: theme.palette.info.light,
                                                                        borderColor: theme.palette.info.main,
                                                                        color: theme.palette.info.dark,
                                                                        cursor: 'help',
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                        {mappedPurpose.processors.length > 0 && (
                                                            <Tooltip 
                                                                title={
                                                                    <Box>
                                                                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                                                            Processors:
                                                                        </Typography>
                                                                        {mappedPurpose.processors.map((processor, index) => (
                                                                            <Typography key={processor.processorId} variant="caption" sx={{ display: 'block' }}>
                                                                                • {processor.processorName}
                                                                            </Typography>
                                                                        ))}
                </Box>
                                                                }
                                                                placement="top"
                                                                arrow
                                                            >
                                                                <Chip
                                                                    label={`${mappedPurpose.processors.length} P`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{
                                                                        fontSize: '0.65rem',
                                                                        height: 18,
                                                                        bgcolor: theme.palette.warning.light,
                                                                        borderColor: theme.palette.warning.main,
                                                                        color: theme.palette.warning.dark,
                                                                        cursor: 'help',
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                        {mappedPurpose.preferences.length > 0 && (
                                                            <Tooltip 
                                                                title={
                                                                    <Box>
                                                                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                                                            Preferences:
                                                                        </Typography>
                                                                        {mappedPurpose.preferences.map((preference, index) => (
                                                                            <Typography key={preference.preferenceId} variant="caption" sx={{ display: 'block' }}>
                                                                                • {preference.preferenceName}
                                                                            </Typography>
                                                                        ))}
                                                                    </Box>
                                                                }
                                                                placement="top"
                                                                arrow
                                                            >
                                                                <Chip
                                                                    label={`${mappedPurpose.preferences.length} PR`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{
                                                                        fontSize: '0.65rem',
                                                                        height: 18,
                                                                        bgcolor: theme.palette.success.light,
                                                                        borderColor: theme.palette.success.main,
                                                                        color: theme.palette.success.dark,
                                                                        cursor: 'help',
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                )}

                                                {/* Expandable content */}
                                                <Collapse in={isCardExpanded(mappedPurpose.MappingId)}>
                                                    <Box sx={{ 
                                                        mt: 2, 
                                                        pt: 2, 
                                                        borderTop: `1px solid ${theme.palette.divider}`,
                                                        bgcolor: theme.palette.background.default,
                                                        borderRadius: 1,
                                                        p: 2,
                                                    }}>
                                                        <Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                fontWeight: 500,
                                                                color: theme.palette.text.primary,
                                                                mb: 1
                                                            }}
                                                        >
                                                            Purpose Details
                                                        </Typography>
                                                        <Typography 
                                                            variant="caption" 
                                                            color="text.secondary"
                                                            sx={{ 
                                                                display: 'block',
                                                                lineHeight: 1.5,
                                                                mb: 2
                                                            }}
                                                        >
                                                            {mappedPurpose.purposeDescription || 'No description available'}
                                                        </Typography>
                                                        
                                                        <Stack spacing={1}>
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                                    Retention Period
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                    {mappedPurpose.retentionPeriodTypeLabel}
                                                                    {mappedPurpose.TimeBasedPeriod && mappedPurpose.timeBasedUnitLabel && (
                                                                        ` - ${mappedPurpose.TimeBasedPeriod} ${mappedPurpose.timeBasedUnitLabel}`
                                                                    )}
                                                                </Typography>
                                                            </Box>
                                                            
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                                    Start Date Type
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                    {mappedPurpose.startDateTypeLabel}
                                                                    {mappedPurpose.CustomStartDate && (
                                                                        ` - ${new Date(mappedPurpose.CustomStartDate).toLocaleDateString()}`
                                                                    )}
                                                                </Typography>
                                                            </Box>

                                                            {mappedPurpose.ExtensionAllowed && (
                                                                <Box>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                                        Extension
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        Allowed{mappedPurpose.ExtensionReason && ` - ${mappedPurpose.ExtensionReason}`}
                                                                    </Typography>
                                                                </Box>
                                                            )}

                                                            {mappedPurpose.LegalRetentionRequired && (
                                                                <Box>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                                        Legal Retention
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        Required - {mappedPurpose.LegalRetentionPeriod} days
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Stack>

                                                        {/* End Conditions */}
                                                        <Box sx={{ mt: 2 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}>
                                                                End Conditions
                                                            </Typography>
                                                            <Grid container spacing={1}>
                                                                <Grid item xs={6}>
                                                                    <Typography variant="body2" sx={{ 
                                                                        fontWeight: mappedPurpose.EndOnPurposeFulfilled ? 500 : 400,
                                                                        color: mappedPurpose.EndOnPurposeFulfilled ? theme.palette.text.primary : theme.palette.text.secondary
                                                                    }}>
                                                                        {mappedPurpose.EndOnPurposeFulfilled ? '✓' : '✗'} Purpose Fulfilled
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <Typography variant="body2" sx={{ 
                                                                        fontWeight: mappedPurpose.EndOnConsentWithdrawn ? 500 : 400,
                                                                        color: mappedPurpose.EndOnConsentWithdrawn ? theme.palette.text.primary : theme.palette.text.secondary
                                                                    }}>
                                                                        {mappedPurpose.EndOnConsentWithdrawn ? '✓' : '✗'} Consent Withdrawn
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <Typography variant="body2" sx={{ 
                                                                        fontWeight: mappedPurpose.EndOnRetentionExpired ? 500 : 400,
                                                                        color: mappedPurpose.EndOnRetentionExpired ? theme.palette.text.primary : theme.palette.text.secondary
                                                                    }}>
                                                                        {mappedPurpose.EndOnRetentionExpired ? '✓' : '✗'} Retention Expired
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <Typography variant="body2" sx={{ 
                                                                        fontWeight: mappedPurpose.EndOnErasureRequested ? 500 : 400,
                                                                        color: mappedPurpose.EndOnErasureRequested ? theme.palette.text.primary : theme.palette.text.secondary
                                                                    }}>
                                                                        {mappedPurpose.EndOnErasureRequested ? '✓' : '✗'} Erasure Requested
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <Typography variant="body2" sx={{ 
                                                                        fontWeight: mappedPurpose.EndOnAccountClosure ? 500 : 400,
                                                                        color: mappedPurpose.EndOnAccountClosure ? theme.palette.text.primary : theme.palette.text.secondary
                                                                    }}>
                                                                        {mappedPurpose.EndOnAccountClosure ? '✓' : '✗'} Account Closure
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </Box>

                                                        {/* Data Elements */}
                                                        {mappedPurpose.dataElements.length > 0 && (
                                                            <Box sx={{ mt: 2 }}>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}>
                                                                    Data Elements ({mappedPurpose.dataElements.length})
                                                                </Typography>
                                                                <Stack spacing={0.5}>
                                                                    {mappedPurpose.dataElements.map((element, index) => (
                                                                        <Box key={element.dataElementId} sx={{ pl: 1 }}>
                                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                                {element.elementName}
                                                                            </Typography>
                                                                            {element.elementDescription && (
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {element.elementDescription}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    ))}
                                                                </Stack>
                                                            </Box>
                                                        )}

                                                        {/* Processors */}
                                                        {mappedPurpose.processors.length > 0 && (
                                                            <Box sx={{ mt: 2 }}>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}>
                                                                    Processors ({mappedPurpose.processors.length})
                                                                </Typography>
                                                                <Stack spacing={0.5}>
                                                                    {mappedPurpose.processors.map((processor, index) => (
                                                                        <Box key={processor.processorId} sx={{ pl: 1 }}>
                                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                                {processor.processorName}
                                                                            </Typography>
                                                                            {processor.processorDescription && (
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {processor.processorDescription}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    ))}
                                                                </Stack>
                                                            </Box>
                                                        )}

                                                        {/* Preferences */}
                                                        {mappedPurpose.preferences.length > 0 && (
                                                            <Box sx={{ mt: 2 }}>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}>
                                                                    Preferences ({mappedPurpose.preferences.length})
                                                                </Typography>
                                                                <Stack spacing={0.5}>
                                                                    {mappedPurpose.preferences.map((preference, index) => (
                                                                        <Box key={preference.preferenceId} sx={{ pl: 1 }}>
                                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                                {preference.preferenceName}
                                                                            </Typography>
                                                                            {preference.preferenceDescription && (
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {preference.preferenceDescription}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    ))}
                                                                </Stack>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Collapse>

                                                {/* Delete button - top right, only show if not published */}
                                                {!isPublished && (
                                                    <Box sx={{ 
                                                        position: 'absolute', 
                                                        top: 8, 
                                                        right: 8,
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s ease-in-out',
                                                        '.MuiCard-root:hover &': {
                                                            opacity: 1,
                                                        }
                                                    }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemovePurposeMapping(mappedPurpose.MappingId)}
                                                            disabled={deletingMappingIds.has(mappedPurpose.MappingId)}
                                                            sx={{
                                                                color: theme.palette.error.main,
                                                                bgcolor: theme.palette.error.light,
                                                                '&:hover': {
                                                                    bgcolor: theme.palette.error.main,
                                                                    color: theme.palette.error.contrastText,
                                                                },
                                                                '&.Mui-disabled': {
                                                                    bgcolor: theme.palette.action.disabledBackground,
                                                                    color: theme.palette.action.disabled,
                                                                },
                                                                width: 28,
                                                                height: 28,
                                                            }}
                                                        >
                                                            {deletingMappingIds.has(mappedPurpose.MappingId) ? (
                                                                <CircularProgress size={16} color="inherit" />
                                                            ) : (
                                                                <DeleteIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    </Box>
                                                )}

                                                {/* Expansion button - bottom right, fixed position */}
                                                <Box sx={{ 
                                                    position: 'absolute', 
                                                    bottom: 8, 
                                                    right: 8,
                                                    zIndex: 1,
                                                }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleCardExpansion(mappedPurpose.MappingId)}
                                                        sx={{
                                                            color: theme.palette.text.secondary,
                                                            transition: 'transform 0.2s ease-in-out',
                                                            transform: isCardExpanded(mappedPurpose.MappingId) ? 'rotate(180deg)' : 'rotate(0deg)',
                                                            width: 28,
                                                            height: 28,
                                                            bgcolor: theme.palette.background.paper,
                                                            border: `1px solid ${theme.palette.divider}`,
                                                            '&:hover': {
                                                                color: theme.palette.primary.main,
                                                                bgcolor: theme.palette.background.paper,
                                                                borderColor: theme.palette.primary.main,
                                                            },
                                                        }}
                                                    >
                                                        <ExpandMoreIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    );
                                })
                            )}
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default PurposesTab; 